import { useState, useEffect } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { safeLocalStorage } from "@/lib/safeStorage";

const DISMISS_KEY = "incomplete_profile_dismissed_at";
const DISMISS_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export function IncompleteProfileBanner() {
  const [isDismissed, setIsDismissed] = useState(false);
  const { userId } = useAuth();

  useEffect(() => {
    // Check if banner was dismissed recently
    const dismissedAt = safeLocalStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const timeSinceDismiss = Date.now() - parseInt(dismissedAt);
      if (timeSinceDismiss < DISMISS_DURATION) {
        setIsDismissed(true);
      } else {
        // Clear old dismiss record
        safeLocalStorage.removeItem(DISMISS_KEY);
      }
    }
  }, []);

  const { data: profile } = useQuery({
    queryKey: ["profile-check", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, email, company_name, phone, invoice_information")
        .eq("id", userId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!userId && !isDismissed,
  });

  const handleDismiss = () => {
    safeLocalStorage.setItem(DISMISS_KEY, Date.now().toString());
    setIsDismissed(true);
  };

  // Check if profile is incomplete
  const isIncomplete =
    profile &&
    (!profile.full_name?.trim() ||
      !profile.company_name?.trim() ||
      !profile.phone?.trim() ||
      !profile.invoice_information?.trim());

  if (!profile || !isIncomplete || isDismissed) {
    return null;
  }

  return (
    <Alert className="border-amber-500/50 bg-amber-500/10">
      <AlertCircle className="h-4 w-4 text-amber-600" />
      <AlertDescription className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">Complete your producer details</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Your crew needs your contact information in briefings. Please complete your profile with
            company name, phone number, and invoice details.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/settings">
            <Button variant="default" size="sm">
              Complete profile
            </Button>
          </Link>
          <Button variant="ghost" size="sm" onClick={handleDismiss} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
